import React, { useState, useEffect, useRef } from 'react';
import { API } from '../App';
import axios from 'axios';
import { PlusCircle, Edit, Trash2, Eye, EyeOff, X, Save, Loader2, Image as ImageIcon, ArrowLeft } from 'lucide-react';

const ax = axios.create({ withCredentials: true });

const BlogAdmin = ({ onBack }) => {
  const [blogs, setBlogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null); // null = list, object = editing
  const [saving, setSaving] = useState(false);

  const fetchBlogs = () => {
    setLoading(true);
    ax.get(`${API}/blogs/admin/all`).then(r => setBlogs(r.data)).finally(() => setLoading(false));
  };

  useEffect(() => { fetchBlogs(); }, []);

  const handleDelete = async (id) => {
    if (!window.confirm('¿Eliminar este blog?')) return;
    await ax.delete(`${API}/blogs/${id}`);
    fetchBlogs();
  };

  const handleTogglePublish = async (blog) => {
    await ax.put(`${API}/blogs/${blog.id}`, { published: !blog.published });
    fetchBlogs();
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      if (editing.id) {
        await ax.put(`${API}/blogs/${editing.id}`, editing);
      } else {
        await ax.post(`${API}/blogs/`, editing);
      }
      setEditing(null);
      fetchBlogs();
    } catch (e) {
      alert(e.response?.data?.detail || 'Error al guardar');
    }
    setSaving(false);
  };

  const newBlog = () => setEditing({ title: '', excerpt: '', content: '', cover_image: '', tags: [], published: false, author: 'LD Holdings' });

  // Editor view
  if (editing) return (
    <div data-testid="blog-editor">
      <div className="flex items-center justify-between mb-6">
        <button onClick={() => setEditing(null)} className="flex items-center gap-2 text-muted-foreground hover:text-foreground text-sm"><ArrowLeft size={16} /> Volver a blogs</button>
        <button onClick={handleSave} disabled={saving || !editing.title} className="flex items-center gap-2 bg-[#D4AF37] text-black px-5 py-2 rounded-lg font-medium hover:bg-[#c29d2e] disabled:opacity-50 text-sm" data-testid="save-blog-btn">
          {saving ? <Loader2 className="animate-spin" size={16} /> : <Save size={16} />} Guardar
        </button>
      </div>

      <div className="space-y-4">
        <div>
          <label className="text-sm text-muted-foreground block mb-1">Título *</label>
          <input value={editing.title} onChange={e => setEditing({ ...editing, title: e.target.value })} className="w-full bg-background border border-border rounded-lg px-4 py-2.5 text-foreground" placeholder="Título del blog" data-testid="blog-title-input" />
        </div>
        <div>
          <label className="text-sm text-muted-foreground block mb-1">Extracto (resumen corto)</label>
          <textarea value={editing.excerpt} onChange={e => setEditing({ ...editing, excerpt: e.target.value })} className="w-full bg-background border border-border rounded-lg px-4 py-2.5 text-foreground" rows={2} placeholder="Resumen breve del blog" />
        </div>
        <div>
          <label className="text-sm text-muted-foreground block mb-1">Imagen de portada (URL)</label>
          <div className="flex gap-2">
            <input value={editing.cover_image} onChange={e => setEditing({ ...editing, cover_image: e.target.value })} className="flex-1 bg-background border border-border rounded-lg px-4 py-2.5 text-foreground text-sm" placeholder="https://..." data-testid="blog-cover-input" />
            {editing.cover_image && <img src={editing.cover_image} alt="" className="w-16 h-16 object-cover rounded-lg border border-border" />}
          </div>
        </div>
        <div>
          <label className="text-sm text-muted-foreground block mb-1">Contenido (HTML)</label>
          <RichEditor value={editing.content} onChange={v => setEditing({ ...editing, content: v })} />
        </div>
        <div className="grid grid-cols-2 gap-4">
          <div>
            <label className="text-sm text-muted-foreground block mb-1">Autor</label>
            <input value={editing.author} onChange={e => setEditing({ ...editing, author: e.target.value })} className="w-full bg-background border border-border rounded-lg px-4 py-2.5 text-foreground text-sm" />
          </div>
          <div>
            <label className="text-sm text-muted-foreground block mb-1">Tags (separados por coma)</label>
            <input value={(editing.tags || []).join(', ')} onChange={e => setEditing({ ...editing, tags: e.target.value.split(',').map(t => t.trim()).filter(Boolean) })} className="w-full bg-background border border-border rounded-lg px-4 py-2.5 text-foreground text-sm" placeholder="finanzas, impuestos" />
          </div>
        </div>
        <label className="flex items-center gap-2 cursor-pointer">
          <input type="checkbox" checked={editing.published} onChange={e => setEditing({ ...editing, published: e.target.checked })} className="rounded" />
          <span className="text-sm text-foreground">Publicar inmediatamente</span>
        </label>
      </div>
    </div>
  );

  // List view
  return (
    <div data-testid="blog-admin-list">
      <div className="flex items-center justify-between mb-6">
        {onBack && <button onClick={onBack} className="flex items-center gap-2 text-muted-foreground hover:text-foreground text-sm"><ArrowLeft size={16} /> Volver</button>}
        <h2 className="text-xl font-bold text-foreground">Gestión de Blogs</h2>
        <button onClick={newBlog} className="flex items-center gap-2 bg-[#D4AF37] text-black px-4 py-2 rounded-lg font-medium hover:bg-[#c29d2e] text-sm" data-testid="new-blog-btn">
          <PlusCircle size={16} /> Nuevo Blog
        </button>
      </div>

      {loading ? (
        <div className="flex justify-center py-12"><Loader2 className="animate-spin" size={32} /></div>
      ) : blogs.length === 0 ? (
        <p className="text-muted-foreground text-center py-12">No hay blogs. Crea el primero.</p>
      ) : (
        <div className="space-y-3">
          {blogs.map(blog => (
            <div key={blog.id} className="flex items-center gap-4 p-4 bg-card border border-border rounded-xl" data-testid={`blog-item-${blog.id}`}>
              {blog.cover_image && <img src={blog.cover_image} alt="" className="w-20 h-14 object-cover rounded-lg shrink-0" />}
              <div className="flex-1 min-w-0">
                <h3 className="font-semibold text-foreground truncate">{blog.title}</h3>
                <div className="flex items-center gap-3 text-xs text-muted-foreground mt-1">
                  <span>{blog.author}</span>
                  <span>{new Date(blog.created_at).toLocaleDateString('es-CO')}</span>
                  <span className={`px-2 py-0.5 rounded-full ${blog.published ? 'bg-emerald-500/20 text-emerald-400' : 'bg-gray-500/20 text-gray-400'}`}>
                    {blog.published ? 'Publicado' : 'Borrador'}
                  </span>
                </div>
              </div>
              <div className="flex items-center gap-2 shrink-0">
                <button onClick={() => handleTogglePublish(blog)} className="p-2 rounded-lg hover:bg-muted text-muted-foreground" title={blog.published ? 'Despublicar' : 'Publicar'}>
                  {blog.published ? <EyeOff size={16} /> : <Eye size={16} />}
                </button>
                <button onClick={() => setEditing(blog)} className="p-2 rounded-lg hover:bg-muted text-muted-foreground" title="Editar" data-testid={`edit-blog-${blog.id}`}>
                  <Edit size={16} />
                </button>
                <button onClick={() => handleDelete(blog.id)} className="p-2 rounded-lg hover:bg-red-500/10 text-red-400" title="Eliminar" data-testid={`delete-blog-${blog.id}`}>
                  <Trash2 size={16} />
                </button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

// Simple rich text editor using contentEditable with toolbar
const RichEditor = ({ value, onChange }) => {
  const editorRef = useRef(null);
  const [showSource, setShowSource] = useState(false);

  const exec = (cmd, val = null) => {
    document.execCommand(cmd, false, val);
    if (editorRef.current) onChange(editorRef.current.innerHTML);
  };

  const insertImage = () => {
    const url = prompt('URL de la imagen:');
    if (url) exec('insertImage', url);
  };

  if (showSource) return (
    <div>
      <div className="flex items-center gap-2 mb-2">
        <button onClick={() => setShowSource(false)} className="text-xs bg-muted px-3 py-1 rounded text-foreground">Vista visual</button>
      </div>
      <textarea value={value} onChange={e => onChange(e.target.value)} className="w-full bg-background border border-border rounded-lg px-4 py-3 text-foreground font-mono text-sm" rows={15} data-testid="blog-content-source" />
    </div>
  );

  return (
    <div>
      <div className="flex flex-wrap items-center gap-1 mb-2 p-2 bg-muted/50 rounded-lg border border-border">
        <button onClick={() => exec('bold')} className="px-2 py-1 rounded hover:bg-muted text-foreground font-bold text-sm" title="Negrita">B</button>
        <button onClick={() => exec('italic')} className="px-2 py-1 rounded hover:bg-muted text-foreground italic text-sm" title="Cursiva">I</button>
        <button onClick={() => exec('formatBlock', 'h2')} className="px-2 py-1 rounded hover:bg-muted text-foreground text-sm font-bold" title="Título">H2</button>
        <button onClick={() => exec('formatBlock', 'h3')} className="px-2 py-1 rounded hover:bg-muted text-foreground text-sm" title="Subtítulo">H3</button>
        <button onClick={() => exec('formatBlock', 'p')} className="px-2 py-1 rounded hover:bg-muted text-foreground text-sm" title="Párrafo">P</button>
        <button onClick={() => exec('insertUnorderedList')} className="px-2 py-1 rounded hover:bg-muted text-foreground text-sm" title="Lista">• Lista</button>
        <button onClick={insertImage} className="px-2 py-1 rounded hover:bg-muted text-muted-foreground" title="Imagen"><ImageIcon size={16} /></button>
        <div className="flex-1" />
        <button onClick={() => setShowSource(true)} className="text-xs text-muted-foreground hover:text-foreground px-2 py-1">HTML</button>
      </div>
      <div
        ref={editorRef}
        contentEditable
        className="w-full bg-background border border-border rounded-lg px-4 py-3 text-foreground min-h-[250px] prose prose-invert prose-sm max-w-none focus:outline-none focus:ring-1 focus:ring-[#D4AF37]/50"
        dangerouslySetInnerHTML={{ __html: value }}
        onInput={() => { if (editorRef.current) onChange(editorRef.current.innerHTML); }}
        data-testid="blog-content-editor"
      />
    </div>
  );
};

export default BlogAdmin;
